import React from 'react';
import {
  Accordion,
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
} from 'semantic-ui-react';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';

import { NotificationDialog, DatePickerCustomButton } from 'Components';
import ReactTable from 'Common/ReactTableWrapper';
import { DatePickerField } from 'Common/SemanticWrapper';
import { ReduxFormButtonGroup } from 'Components/forms/ReduxFormButtonGroup';
import { YesNoDialog } from 'Components/dialogs/YesNoDialog';
import { DATE_FORMAT_FOR_UI } from 'Common/Constants';
import { USAGE_LIST_FORM_NAME } from 'Common/FormEnum';
import { UsageEnum } from 'Common/FieldEnum';
import { TableNamesEnum } from 'Common/Constants/Enumeration/TableEnum';
import {
  StatusEnum,
  PrintOptionsEnum,
} from 'Common/Constants/Enumeration/UsageListEnum';
import { LoadingPage } from 'Components/loading/LoadingPage';
import { GridEnum } from 'Common/Constants/Enumeration/GridEnum';
import { SiteComponent } from 'Common/SiteComponent';
import { SiteTypeEnum } from 'Common/Constants/Enumeration/SiteTypeEnum';

const UsageList = props => {
  const viewStatusOptions = [
    { value: StatusEnum.All, label: 'All' },
    { value: StatusEnum.Open, label: 'Open' },
    { value: StatusEnum.Closed, label: 'Closed' },
  ];

  const printOptions = [
    { value: PrintOptionsEnum.ItemCode, label: 'Item Code' },
    { value: PrintOptionsEnum.ItemDescription, label: 'Item Description' },
  ];
  const {
    columns,
    reactTableRef,
    usageListData,
    setSelectedSite,
    fromDateMax,
    toDateMin,
    onChangeOfFromDT,
    onChangeOfToDT,
    gridPreferenceLoading,
    gridPreferenceSubmitting,
    fetchingSites,
    loading,
    printUsage,
    setPrintTypeOption,
    setShowStatusOption,
    clearFilters,
    selectedUsageRecord,
    handleDeleteDialog,
    showDeleteModel,
    showDeleteConfirm,
    closeDeleteOrderConfirm,
    printLoading,
    activeIndexForFilter,
    handleClick,
    activeIndexForSort,
  } = props;

  let deleteQuestion = `Are you sure you would like to permanently delete usageId# ${selectedUsageRecord} ?`;
  if (selectedUsageRecord && selectedUsageRecord.length > 1) {
    deleteQuestion =
      'Are you sure you would like to permanently delete all checked usages?';
  }

  return printLoading ? (
    <LoadingPage />
  ) : (
    <Form>
      <Accordion fluid styled>
        <Accordion.Title
          active={!activeIndexForFilter}
          index={0}
          option={0}
          onClick={handleClick}
        >
          <Icon name="dropdown" />
          <span className="text-orange">Filter By</span>
        </Accordion.Title>
        <Accordion.Content active={!activeIndexForFilter}>
          <Grid className="grid-spacing">
            <Grid.Row columns={2}>
              <Grid.Column width={8}>
                <div style={{ paddingTop: 25 }}>
                  <Header as="h4">Date Range</Header>
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={4}>
              <Grid.Column>
                <Field
                  component={DatePickerField}
                  customInput={<DatePickerCustomButton />}
                  label={'From:'}
                  dateFormat={DATE_FORMAT_FOR_UI}
                  name={UsageEnum.BeginDT}
                  onChange={onChangeOfFromDT}
                  maxDate={fromDateMax}
                  type={'text'}
                  dropdownMode="select"
                />
              </Grid.Column>
              <Grid.Column>
                <Field
                  component={DatePickerField}
                  customInput={<DatePickerCustomButton />}
                  label={'To:'}
                  dateFormat={DATE_FORMAT_FOR_UI}
                  name={UsageEnum.EndDT}
                  type={'text'}
                  onChange={onChangeOfToDT}
                  minDate={toDateMin}
                  dropdownMode="select"
                />
              </Grid.Column>
              <Grid.Column>
                <SiteComponent
                  name={UsageEnum.Site}
                  label={'Site'}
                  onChange={setSelectedSite}
                  isForm={true}
                  siteMasterTypeIds={[
                    SiteTypeEnum.School,
                    SiteTypeEnum.Kitchen,
                  ]}
                  isAllRequired={true}
                  checkIsLicensed={false}
                  isAllValueZero={true}
                />
              </Grid.Column>
              <Grid.Column
                style={{ marginTop: 45 }}
                floated={'right'}
                textAlign={'right'}
              >
                <Button
                  content="Clear Filters"
                  onClick={e => clearFilters()}
                  type={'button'}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column width={8}>
                <div style={{ paddingTop: 25 }}>
                  <Header as="h4">View Status</Header>
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <ReduxFormButtonGroup
                  options={viewStatusOptions}
                  type={'button'}
                  formName={USAGE_LIST_FORM_NAME}
                  fieldName={UsageEnum.ViewStatusOptions}
                  onChange={setShowStatusOption}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Accordion.Content>
        <Accordion.Title
          active={!activeIndexForSort}
          index={0}
          option={1}
          onClick={handleClick}
        >
          <Icon name="dropdown" />
          <span className="text-orange">Sort By</span>
        </Accordion.Title>
        <Accordion.Content active={!activeIndexForSort}>
          <Grid>
            <Grid.Row columns={2} className={'pt10'}>
              <Grid.Column verticalAlign={'bottom'}>
                <ReduxFormButtonGroup
                  options={printOptions}
                  formName={USAGE_LIST_FORM_NAME}
                  fieldName={UsageEnum.PrintOptions}
                  onChange={setPrintTypeOption}
                  type={'button'}
                />
              </Grid.Column>
              <Grid.Column floated={'right'} textAlign={'right'}>
                <Button
                  disabled={loading}
                  content="Print"
                  type={'button'}
                  style={{ marginLeft: 10 }}
                  onClick={printUsage}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Accordion.Content>
      </Accordion>
      <Divider horizontal />
      <Grid>
        <Grid.Row columns={1} className={'remove-spacing'}>
          <Grid.Column>
            <ReactTable
              reactTableRef={reactTableRef}
              defaultPageSize={20}
              showPageSizeOptions={true}
              minRows={0}
              data={usageListData || []}
              noDatatext={loading ? '' : 'No rows found'}
              resizable={false}
              loadingText={loading ? 'Loading...' : ''}
              className="-striped -highlight"
              columns={columns}
              loading={
                loading ||
                fetchingSites ||
                gridPreferenceLoading ||
                gridPreferenceSubmitting
              }
              draggableColumns={true}
              showHideColumn={true}
              menu={'hasCogs'}
              saveState={{
                tableName: TableNamesEnum.USAGE_LIST_TABLE,
              }}
              hasSelectAllColumn={true}
              gridName={GridEnum.INV_UsageList_View}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <YesNoDialog
        showDialog={showDeleteModel}
        title={'Delete Usage'}
        question={deleteQuestion}
        yesCallback={() => {
          handleDeleteDialog(true);
        }}
        noCallback={() => {
          handleDeleteDialog(false);
        }}
      />
      <NotificationDialog
        title={'Delete Usage'}
        showDialog={showDeleteConfirm}
        message={'Please only select open usage(s) to delete'}
        onOkClick={closeDeleteOrderConfirm}
      />
    </Form>
  );
};

UsageList.propTypes = {
  onChangeOfFromDT: PropTypes.func,
  onChangeOfToDT: PropTypes.func,
  printUsage: PropTypes.func,
  setPrintTypeOption: PropTypes.func,
  setShowStatusOption: PropTypes.func,
  clearFilters: PropTypes.func,
  selectedUsageRecord: PropTypes.func,
  handleDeleteDialog: PropTypes.func,
  handleClick: PropTypes.func,
  setSelectedSite: PropTypes.func,
  columns: PropTypes.array.isRequired,
  usageListData: PropTypes.array.isRequired,
  fromDateMax: PropTypes.object,
  toDateMin: PropTypes.object,
  gridPreferenceLoading: PropTypes.bool,
  gridPreferenceSubmitting: PropTypes.bool,
  fetchingSites: PropTypes.bool,
  loading: PropTypes.bool,
  showDeleteModel: PropTypes.bool,
  showDeleteConfirm: PropTypes.bool,
  closeDeleteOrderConfirm: PropTypes.func,
  printLoading: PropTypes.bool,
  reactTableRef: PropTypes.object,
  activeIndexForFilter: PropTypes.number,
  activeIndexForSort: PropTypes.number,
};
UsageList.defaultProps = {
  onChangeOfFromDT: () => {},
  onChangeOfToDT: () => {},
  printUsage: () => {},
  setPrintTypeOption: () => {},
  setShowStatusOption: () => {},
  clearFilters: () => {},
  selectedUsageRecord: () => {},
  handleDeleteDialog: () => {},
  handleClick: () => {},
  setSelectedSite: () => {},
  reactTableRef: null,
  activeIndexForFilter: 0,
  fromDateMax: null,
  toDateMin: null,
  gridPreferenceLoading: false,
  gridPreferenceSubmitting: false,
  fetchingSites: false,
  loading: false,
  showDeleteModel: false,
  showDeleteConfirm: false,
  closeDeleteOrderConfirm: () => {},
  printLoading: false,
};
export default UsageList;



// WEBPACK FOOTER //
// ./src/pages/inventory/transaction/usage/components/usageList/UsageList.js